import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Table, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import { Translate, getSortState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getAdminDocuments } from '../point-details/viewDocuments.reducer';
import { getCategories } from 'app/entities/achievements/achievements.reducer';
import { updatePdfStatus, reset } from './pdfStatus.reducer';

export const Listing = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  // const [confirmModal, setConfirmModal] = useState<{ open: boolean; fileId?: string; status?: 'APPROVED' | 'REJECTED' }>({ open: false });
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; id?: number; status?: 'APPROVED' | 'REJECTED' }>({ open: false });
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(location, ITEMS_PER_PAGE, 'id'), location.search)
  );
  const [rejectReason, setRejectReason] = useState('');

  const loading = useAppSelector(state => state.viewDocuments.loading);
  // const docUrl = 'http://localhost:8082/doc/'; //local
  // const imageUrl = 'http://localhost:8082/image/'; //local
  const imageUrl = '/image/'; //dev or Prod
  const docUrl = '/doc/'; //dev or Prod

  const { entities } = useAppSelector(state => state.viewDocuments);
  const totalItems = entities.page?.totalElements;
  const pointDetailsList = entities?.page?.content;
  const pendingStatusCount = entities?.pendingStatusCount;
  const approvedStatusCount = entities?.approvedStatusCount;
  const rejectedStatusCount = entities?.rejectedStatusCount;
  const categoriesData = useAppSelector(state => state.achievements.entities);
  const pdfStatusState = useAppSelector(state => state.pdfStatus);

  useEffect(() => {
    dispatch(getCategories());
  }, []);

  const getAllDocuments = () => {
    dispatch(
      getAdminDocuments({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        status: statusFilter,
        categoryId: selectedCategoryId,
      })
    );
  };
  useEffect(() => {
    getAllDocuments();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, statusFilter, selectedCategoryId]);

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const statusColor = status => {
    status = status.toLowerCase();
    if (status === 'pending') return { color: '#92400E', bgColor: '#FEF7D6' };
    if (status === 'approved') return { color: '#166534', bgColor: '#D4F7DC' };
    if (status === 'rejected') return { color: '#991B1B', bgColor: '#FFEEEE' };
  };

  const statusButtons = [
    { label: 'Pending', apiValue: 'pending', color: '#92400E', bgColor: '#FEF7D6', count: pendingStatusCount },
    { label: 'Approved', apiValue: 'approved', color: '#166534', bgColor: '#D4F7DC', count: approvedStatusCount },
    { label: 'Addl info rqd', apiValue: 'rejected', color: '#991B1B', bgColor: '#FFEEEE', count: rejectedStatusCount },
  ];

  // const openConfirmModal = (fileId: string, status: 'APPROVED' | 'REJECTED') => {
  //   setRejectReason(''); // reset every time
  //   setConfirmModal({ open: true, fileId, status });
  // };
  const openConfirmModal = (id: number, status: 'APPROVED' | 'REJECTED') => {
    setRejectReason(''); // reset every time
    setConfirmModal({ open: true, id, status });
  };

  // const confirmAction = () => {
  //     if (!confirmModal.fileId || !confirmModal.status) return;

  const confirmAction = () => {
    if (!confirmModal.id || !confirmModal.status) return;

    if (confirmModal.status.toLowerCase() === 'rejected' && (!rejectReason || !rejectReason.trim())) {
      return;
    }

    dispatch(
      updatePdfStatus({
        // fileId: confirmModal.fileId,
        id: confirmModal.id,
        status: confirmModal.status,
        rejectionReason: confirmModal.status === 'REJECTED' ? rejectReason : undefined,
      })
    ).then(() => {
      dispatch(reset());
      getAllDocuments();
      setConfirmModal({ open: false });
    });
  };

  return (
    <div className="card point-allocation-tab">
      {loading && (
        <div
          className="overlay-loader d-flex flex-column align-items-center justify-content-center"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 9999,
          }}
        >
          <Spinner className="body-loader-icon" />
        </div>
      )}
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
        {/* Title */}
        <h5 className="fw-semibold text-center text-lg-start mb-0 text-nowrap">PDF Approvals</h5>

        {/* Filters Section */}
        <div className="d-flex flex-wrap justify-content-center justify-content-lg-end align-items-center gap-2 w-100 w-lg-auto">
          {/* Category Dropdown */}
          <select
            className="form-select form-select-sm w-100 w-sm-auto"
            style={{ maxWidth: '220px', borderRadius: '10px' }}
            value={selectedCategoryId ?? ''}
            onChange={e => setSelectedCategoryId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">All Categories</option>
            {categoriesData?.map(category => (
              <option key={category.id} value={category.id}>
                {category.displayName}
              </option>
            ))}
          </select>

          {/* Status Buttons */}
          {statusButtons.map(btn => (
            <Button
              key={btn.label}
              outline
              size="sm"
              active={statusFilter === btn.apiValue}
              onClick={() => {
                setStatusFilter(btn.apiValue);
                setPaginationState(prev => ({
                  ...prev,
                  activePage: 1,
                }));
              }}
              className="flex-shrink-0"
              style={{
                borderRadius: '10px',
                whiteSpace: 'nowrap',
                color: statusFilter === btn.apiValue ? '#fff' : '#667085',
                backgroundColor: statusFilter === btn.apiValue ? '#f7c723' : 'transparent',
                borderColor: statusFilter === btn.apiValue ? '#FACC15' : '#E1E3E5',
              }}
            >
              {btn.label}: <span style={{ color: btn.color }}>{btn.count}</span>
            </Button>
          ))}
        </div>
      </div>

      <Table responsive hover className="align-middle" size="lg">
        <thead className="table-light">
          <tr>
            <th>Username</th>
            <th>File Name</th>
            <th>Category</th>
            {/* <th>Description</th> */}
            <th>Points</th>
            <th>Status</th>
            <th>Submitted</th>

            {statusFilter.toLowerCase() === 'rejected' && <th>Addl info rqd</th>}
            {statusFilter.toLowerCase() === 'pending' && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {pointDetailsList?.map((item, index) => {
            const isImage = /\.(jpg|jpeg|png)$/i.test(item.fileName);
            const fileUrl = isImage ? imageUrl + item.fileName : docUrl + item.fileName;

            return (
              <tr key={index}>
                <td>{item?.userName}</td>
                <td>
                  {item.externalLink ? (
                    <a
                      href={item.externalLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-none"
                      title={item.externalLink}
                    >
                      <div className="d-flex align-items-center gap-2 text-primary fw-medium">
                        <FontAwesomeIcon icon="link" />
                        <span>View Link</span>
                      </div>
                    </a>
                  ) : (
                    <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                      <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={isImage ? 'file-image' : 'file-pdf'} className={isImage ? 'text-success' : 'text-danger'} />
                        <span className="text-truncate" style={{ maxWidth: '220px' }}>
                          {item.originalFileName || item.fileName}
                        </span>
                      </div>
                    </a>
                  )}
                </td>
                <td>
                  <div className="align-items-center">{item.displayName}</div>
                </td>
                {/* <td>
                <div className="text-muted small text-truncate">{item.description}</div>
              </td> */}
                <td className="text-warning">
                  <div className="d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon="star" />
                    <span>{item.points}</span>
                  </div>
                </td>
                <td>
                  {(() => {
                    const colorData = statusColor(item.status);
                    return (
                      <span
                        className="px-3 py-2 badge rounded-pill"
                        style={{
                          backgroundColor: colorData.bgColor,
                          color: colorData.color,
                        }}
                      >
                        {/* {item.status} */}
                        {item.status?.toLowerCase() === 'rejected' ? 'Addl info rqd' : item.status}
                      </span>
                    );
                  })()}
                </td>
                <td>
                  <div className="text-muted d-flex">{item?.submittedDate ? moment(item.submittedDate).format('DD/MM/YYYY') : ''}</div>
                </td>

                {statusFilter.toLowerCase() === 'rejected' && <td className="text-muted small">{item.rejectionReason || '-'}</td>}

                {statusFilter.toLowerCase() === 'pending' && (
                  <td>
                    <div className="d-flex gap-3">
                      <Button
                        color="link"
                        className="p-0 text-success"
                        title="Approve"
                        disabled={pdfStatusState.updating}
                        // onClick={() => openConfirmModal(item.fileName, 'APPROVED')}
                        onClick={() => openConfirmModal(item.id, 'APPROVED')}
                      >
                        <FontAwesomeIcon icon="check" size="lg" />
                      </Button>

                      <Button
                        color="link"
                        className="p-0 text-danger"
                        title="Addl info rqd"
                        disabled={pdfStatusState.updating}
                        // onClick={() => openConfirmModal(item.fileName, 'REJECTED')}
                        onClick={() => openConfirmModal(item.id, 'REJECTED')}
                      >
                        <FontAwesomeIcon icon="times" size="lg" />
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
          {pointDetailsList?.length === 0 && (
            <tr>
              <td colSpan={8} className="text-center py-5">
                <div className="d-flex flex-column align-items-center gap-2 text-muted">
                  <FontAwesomeIcon icon="file-circle-xmark" size="2x" />
                  <span className="fw-semibold fs-6">No documents have been uploaded yet</span>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {totalItems ? (
        <div className={pointDetailsList && pointDetailsList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}

      <Modal isOpen={confirmModal.open} toggle={() => setConfirmModal({ open: false })} centered>
        <ModalHeader toggle={() => setConfirmModal({ open: false })}>Confirm Action</ModalHeader>

        <ModalBody className="text-center">
          <FontAwesomeIcon
            icon={confirmModal.status === 'APPROVED' ? 'check-circle' : 'times-circle'}
            size="3x"
            className={confirmModal.status === 'APPROVED' ? 'text-success mb-3' : 'text-danger mb-3'}
          />

          <p className="mb-0">
            Are you sure you want to{' '}
            <strong>{confirmModal.status?.toLowerCase() === 'rejected' ? 'Addl info rqd' : confirmModal.status?.toLowerCase()}</strong> this
            document?
          </p>

          {confirmModal.status === 'REJECTED' && (
            <div className="mt-3 text-start">
              <label className="form-label fw-semibold">Reason</label>
              <textarea
                className="form-control"
                rows={3}
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="Write reason..."
              />
            </div>
          )}
        </ModalBody>

        <ModalFooter className="justify-content-center">
          <Button color="secondary" outline onClick={() => setConfirmModal({ open: false })}>
            Cancel
          </Button>
          <Button
            color={confirmModal.status === 'APPROVED' ? 'primary' : 'danger'}
            onClick={confirmAction}
            disabled={pdfStatusState.updating || (confirmModal.status === 'REJECTED' && !rejectReason.trim())}
          >
            {pdfStatusState.updating ? 'Processing...' : 'Yes, Confirm'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Listing;
